import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { QRCodeCanvas } from 'qrcode.react';

import DataTable from 'react-data-table-component';
import ViewSection from './ViewSection';
import ConfirmPrompt from './ConfirmPrompt';
import TableLoader from './TableLoader';
import Modal from './Modal';
import ProjectAllocationForm from './ProjectAllocationForm';
import AddRequiredLicenseForm from './AddRequiredLicenseForm';
import AddRequiredDocumentForm from './AddRequiredDocumentForm';
import TimeEntryForm from './TimeEntries/TimeEntryForm';
import ProjectAdditionalInformationForm from './ProjectAdditionalnformationForm';

import generateTimeEntryColumns from './TimeEntries/generateTimeEntryColumns';

import allocationService from '../services/allocationService';
import projectService from '../services/projectService';
import useAllocations from '../hooks/useAllocations';
import useProject from '../hooks/useProject';
import useTimeEntries from '../hooks/domain/timeEntries/useTimeEntries';

import timeEntryService from '../services/timeEntryService';
import labourerService from '../services/labourerService';

import { formatDateToSydneyTimeZone } from '../utils/dateUtils';

const AdminProjectView = ({ project, onCloseClicked, onUpdateClicked, onDeleteClicked }) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { data: allocationData, isLoading: isLoadingAllocations, mutate } = useAllocations({ projectId: project._id, page, perPage });

  const { data: projectData, mutate: mutateProject } = useProject({ projectId: project._id });
  const fetchedProject = projectData?.project;

  const allocations = allocationData?.allocations;
  const allocationCount = allocationData?.count;

  const [selectedAllocation, setSelectedAllocation] = useState(null);

  const [isAddLicenseModalOpen, setIsAddLicenseModalOpen] = useState(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [isAllocationsViewOpen, setIsAllocationsViewOpen] = useState(false);
  const [isConfirmRemoveAllocationModalOpen, setIsConfirmRemoveAllocationModalOpen] = useState(false);
  const [isAdditionalInformationModalOpen, setIsAdditionalInformationModalOpen] = useState(false);
  const [isAddProjectTimeEntryModalOpen, setIsAddProjectTimeEntryModalOpen] = useState(false);

  const qrRef = useRef(null);
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);
  const [isInvalidatingQr, setIsInvalidatingQr] = useState(false);
  const [isConfirmInvalidateQrOpen, setIsConfirmInvalidateQrOpen] = useState(false);

  const [isAddingLicenses, setIsAddingLicenses] = useState(false);
  const [isAddingDocuments, setIsAddingDocuments] = useState(false);
  const [isRemovingLicense, setIsRemovingLicense] = useState(false);
  const [isRemovingDocument, setIsRemovingDocument] = useState(false);
  const [isUpdatingAdditionalInformation, setIsUpdatingAdditionalInformation] = useState(false);

  const [projectTimeEntriesPage, setProjectTimeEntriesPage] = useState(1);
  const [projectTimeEntriesPerPage, setProjectTimeEntriesPerPage] = useState(5);

  const [labourers, setLabourers] = useState([]);

  const [isSubmittingTimeEntryForm, setIsSubmittingTimeEntryForm] = useState(false);
  const [isConfirmDeleteTimeEntryModalOpen, setIsConfirmDeleteTimeEntryModalOpen] = useState(false);

  const [selectedProjectTimeEntry, setSelectedProjectTimeEntry] = useState(null);

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleRemoveAllocationConfirmation = async () => {
    setIsConfirmRemoveAllocationModalOpen(false);

    await allocationService.deleteAllocation(selectedAllocation._id);

    mutate();
  }

  const handleRemoveAllocationClicked = async (allocation) => {
    setSelectedAllocation(allocation);

    setIsConfirmRemoveAllocationModalOpen(true);
  }

  const handleAllocationCompleted = async () => {
    setIsAllocationsViewOpen(false);
    mutate();
  }

  const handleRemoveRequiredLicense = async (license) => {
    if (isRemovingLicense) return toast.error('Please wait for the current operation to complete');

    setIsRemovingLicense(true);

    const newLicenses = fetchedProject.requiredLicenses
      .filter(l => l._id !== license._id)
      .map(l => l._id);

    await projectService.updateProject({
      projectId: project._id,
      updateData: {
        requiredLicenses: newLicenses
      }
    });

    await mutateProject();

    setIsRemovingLicense(false);
    toast.success('License removed');
  }

  const handleRemoveRequiredDocument = async (documentType) => {
    if (isRemovingDocument) return toast.error('Please wait for the current operation to complete');

    setIsRemovingDocument(true);

    await projectService.updateProject({
      projectId: project._id,
      updateData: {
        requiredDocuments: fetchedProject.requiredDocuments.filter(d => d._id !== documentType._id)
      }
    });

    await mutateProject();

    setIsRemovingDocument(false);
    toast.success('Document type removed');
  }

  const allocationColumns = [
    {
      name: 'Labourer',
      selector: row => row.labourer.user.fullName
    },
    {
      name: 'Start Date',
      selector: row => row.startDate,
      format: row => formatDateToSydneyTimeZone(row.startDate)
    },
    {
      name: 'End Date',
      selector: row => row.endDate,
      format: row => formatDateToSydneyTimeZone(row.endDate)
    },
    {
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleRemoveAllocationClicked(row)}
            className='p-1 font-bold cursor-pointer rounded-md bg-red-600 text-white'
          >
            Remove
          </button>
        </div>
      )
    }
  ];

  const licenseColumns = [
    {
      name: 'HRWL',
      selector: row => row.hrwlCode
    },
    {
      name: 'Name',
      selector: row => row.name
    },
    {
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleRemoveRequiredLicense(row)}
            className='py-1 px-2 text-xs font-bold cursor-pointer rounded-md bg-red-600 text-white'
          >
            Remove
          </button>
        </div>
      )
    }
  ];

  const documentColumns = [
    {
      name: 'Document type',
      selector: row => row.name
    },
    {
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleRemoveRequiredDocument(row)}
            className='py-1 px-2 text-xs font-bold cursor-pointer rounded-md bg-red-600 text-white'
          >
            Remove
          </button>
        </div>
      )
    }
  ]

  const handleLicenseFormSubmit = async (licenses) => {
    const newLicenses = fetchedProject.requiredLicenses.map(l => l._id);

    setIsAddingLicenses(true);

    await projectService.updateProject({
      projectId: project._id,
      updateData: {
        requiredLicenses: newLicenses.concat(licenses)
      }
    });

    await mutateProject();

    setIsAddingLicenses(false);
    setIsAddLicenseModalOpen(false);

    toast.success('License added');
  };

  const handleDocumentFormSubmit = async (documentTypes) => {
    setIsAddingDocuments(true);

    await projectService.updateProject({
      projectId: project._id,
      updateData: {
        requiredDocuments: [...fetchedProject.requiredDocuments, ...documentTypes]
      }
    });

    await mutateProject();
    setIsAddingDocuments(false);

    setIsAddDocumentModalOpen(false);

    toast.success('Documents added');
  };

  const handleAdditionalInformationSubmit = async (additionalInformation) => {
    try {
      setIsUpdatingAdditionalInformation(true);

      await projectService.updateProject({
        projectId: project._id,
        updateData: {
          additionalInformation
        }
      });

      mutateProject();

      toast.success('Additional information updated');
    } catch (error) {
      toast.error('Failed to update additional information. Please try again');
    } finally {
      setIsUpdatingAdditionalInformation(false);
      setIsAdditionalInformationModalOpen(false);
    }
  };

  const handleGenerateQr = async () => {
    try {
      setIsGeneratingQr(true);
      await projectService.generateQrToken(project._id);
      await mutateProject();
      toast.success('QR code generated');
    } catch (error) {
      toast.error('Failed to generate QR code. Please try again.');
    } finally {
      setIsGeneratingQr(false);
    }
  };

  const handleInvalidateQr = async () => {
    try {
      setIsInvalidatingQr(true);
      await projectService.invalidateQrToken(project._id);
      await mutateProject();
      toast.success('QR code invalidated');
    } catch (error) {
      toast.error('Failed to invalidate QR code. Please try again.');
    } finally {
      setIsInvalidatingQr(false);
      setIsConfirmInvalidateQrOpen(false);
    }
  };

  const handleDownloadQr = () => {
    const canvas = qrRef.current;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.download = `${project.name}-qr.png`;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${process.env.REACT_APP_APP_URL}/scan/${fetchedProject.qrToken}`
      );
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const fetchLabourers = async () => {
    const { labourers } = await labourerService.getLabourers({ page: 1, perPage: 999 });

    setLabourers(labourers);
  };

  const {
    timeEntries,
    count: timeEntryCount,
    isLoading: isLoadingTimeEntries,
    fetchTimeEntries,
  } = useTimeEntries({
    projectId: project._id,
    page: projectTimeEntriesPage,
    perPage: projectTimeEntriesPerPage,
    onError: () => toast.error('Something went wrong while loading time entries'),
  });

  const handleProjectTimeEntriesPageChange = async (page) => {
    setProjectTimeEntriesPage(page);
  };

  const handleProjectTimeEntriesPerPageChange = async (page, newPerPage) => {
    setProjectTimeEntriesPerPage(newPerPage);
    setProjectTimeEntriesPage(page);
  };

  const handleSubmitTimeEntryForm = async (payload) => {
    setIsSubmittingTimeEntryForm(true);

    payload.projectId = project._id;
    payload.entryType = 'manual';

    try {
      if (selectedProjectTimeEntry) {
        await timeEntryService.updateTimeEntry(selectedProjectTimeEntry._id, payload);
      } else {
        await timeEntryService.createTimeEntry(payload);
      }

      await fetchTimeEntries();
      toast.success(selectedProjectTimeEntry ? 'Time entry edited' : 'Time entry created');

      setIsAddProjectTimeEntryModalOpen(false);
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong while submitting the time entry form. Please try again');
    } finally {
      setIsSubmittingTimeEntryForm(false);
    }
  };

  const handleEditTimeEntry = async (timeEntry) => {
    setSelectedProjectTimeEntry(timeEntry);
    setIsAddProjectTimeEntryModalOpen(true);
  };

  const handleDeleteTimeEntry = async () => {
    try {
      await timeEntryService.removeTimeEntry(selectedProjectTimeEntry._id);
      await fetchTimeEntries();

      setSelectedProjectTimeEntry(null);
      setIsConfirmDeleteTimeEntryModalOpen(false);
      toast.success('Time entry removed');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong while deleting the time entry. Please try again');
    }
  };

  const handleRemoveTimeEntryClick = (timeEntry) => {
    setSelectedProjectTimeEntry(timeEntry);
    setIsConfirmDeleteTimeEntryModalOpen(true);
  };

  const handleCloseTimeEntryFormModal = () => {
    setIsAddProjectTimeEntryModalOpen(false);
    setSelectedProjectTimeEntry(null);
  };

  useEffect(() => {
    fetchTimeEntries();
    fetchLabourers();
  }, [project, projectTimeEntriesPage, projectTimeEntriesPerPage]);

  const labourerSelectOptions = useMemo(() => {
    return labourers.map(labourer => ({
      value: labourer._id,
      label: labourer.user.fullName
    }));
  }, [labourers]);

  return (
    <div className="bg-white rounded-lg border border-ase-blue w-full md:w-auto md:min-w-[900px]">
      <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
        <p>{project.name}</p>

        <p
          onClick={onCloseClicked}
          className="font-bold cursor-pointer text-2xl"
        >
          X
        </p>
      </div>

      <div className="flex flex-col gap-2 p-4">
        <p><b>Address:</b> {project.address}</p>
        <p><b>Status: </b> {project.status}</p>

        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <p><b>Start date:</b> {formatDateToSydneyTimeZone(project.startDate)}</p>
          <p><b>End date:</b> {formatDateToSydneyTimeZone(project.endDate)}</p>
        </div>

        <div className='flex items-center gap-2 mt-1'>
          <button
            onClick={onUpdateClicked}
            className='ase-button-primary text-sm'
          >
            Update
          </button>

          <button
            onClick={onDeleteClicked}
            className='ase-button-danger text-sm'
          >
            Delete
          </button>
        </div>
      </div>

      <ViewSection
        expandable={true}
        title='QR Code'
        actions={(
          <div className="flex gap-2">
            {fetchedProject?.qrToken && (
              <>
                <button
                  onClick={handleDownloadQr}
                  className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'
                >
                  Download
                </button>
                <button
                  onClick={handleCopyLink}
                  className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'
                >
                  Copy Link
                </button>
                <button
                  onClick={() => setIsConfirmInvalidateQrOpen(true)}
                  disabled={isInvalidatingQr}
                  className='bg-white text-red-500 font-semibold text-xs py-1 px-2 rounded-md'
                >
                  Invalidate
                </button>
              </>
            )}
            {!fetchedProject?.qrToken && (
              <button
                onClick={handleGenerateQr}
                disabled={isGeneratingQr}
                className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'
              >
                {isGeneratingQr ? 'Generating…' : 'Generate'}
              </button>
            )}
          </div>
        )}
        content={(
          <div className="p-4 flex flex-col items-center">
            {fetchedProject?.qrToken ? (
              <>
                <QRCodeCanvas
                  value={`${process.env.REACT_APP_APP_URL}/scan/${fetchedProject.qrToken}`}
                  size={200}
                  className="mb-3"
                />
                <p className="text-xs text-gray-400 text-center">
                  Scan to clock in/out at {project.name}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500 py-4">
                No QR code generated. Click "Generate" to create one.
              </p>
            )}
          </div>
        )}
      />

      <ViewSection
        expandable={true}
        title='Additional Information'
        actions={(
          <button
            onClick={() => setIsAdditionalInformationModalOpen(true)}
            className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'
          >
            Update
          </button>
        )}
        content={(
          <div
            className="p-4 tiptap"
            dangerouslySetInnerHTML={{ __html: fetchedProject?.additionalInformation }}
          >
          </div>
        )}
      />

      <ViewSection
        title='Allocations'
        expandable={true}
        actions={(
          <button
            onClick={() => setIsAllocationsViewOpen(true)}
            className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'
          >
            Add
          </button>
        )}
        content={(
          <DataTable
            columns={allocationColumns}
            data={allocations}
            striped
            highlightOnHover
            pagination
            paginationServer
            pointerOnHover
            progressPending={isLoadingAllocations}
            progressComponent={<TableLoader />}
            paginationTotalRows={allocationCount}
            paginationRowsPerPageOptions={[10, 20, 50]}
            paginationPerPage={perPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        )}
      />

      <ViewSection
        title='Time entries'
        expandable={true}
        actions={(
          <button
            onClick={() => setIsAddProjectTimeEntryModalOpen(true)}
            className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'
          >
            Add
          </button>
        )}
        content={(
          <DataTable
            columns={generateTimeEntryColumns({
              showProjectField: false,
              onEditClicked: handleEditTimeEntry,
              onRemoveClicked: handleRemoveTimeEntryClick
            })}
            data={timeEntries}
            striped
            highlightOnHover
            pagination
            paginationServer
            pointerOnHover
            progressPending={isLoadingTimeEntries}
            progressComponent={<TableLoader />}
            paginationTotalRows={timeEntryCount}
            paginationRowsPerPageOptions={[5, 10, 20, 50]}
            paginationPerPage={projectTimeEntriesPerPage}
            onChangePage={handleProjectTimeEntriesPageChange}
            onChangeRowsPerPage={handleProjectTimeEntriesPerPageChange}
          />
        )}
      />

      <ViewSection
        title='Required licenses'
        actions={(
          <button
            onClick={setIsAddLicenseModalOpen}
            className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'
          >
            Add
          </button>
        )}
        content={(
          <DataTable
            columns={licenseColumns}
            data={fetchedProject?.requiredLicenses}
            striped
            highlightOnHover
            pagination
          />
        )}
        expandable={true}
      />

      <ViewSection
        title='Required documents'
        expandable={true}
        actions={(
          <button
            onClick={e => setIsAddDocumentModalOpen(true)}
            className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'
          >
            Add
          </button>
        )}
        content={(
          <DataTable
            columns={documentColumns}
            data={fetchedProject?.requiredDocuments}
            striped
            highlightOnHover
            pagination
          />
        )}
      />

      <Modal isOpen={isAddLicenseModalOpen} overflowType="visible">
        <AddRequiredLicenseForm
          disabledLicenses={fetchedProject?.requiredLicenses}
          isSubmitting={isAddingLicenses}
          onSubmit={handleLicenseFormSubmit}
          onCloseClicked={e => setIsAddLicenseModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isAddDocumentModalOpen} overflowType="visible">
        <AddRequiredDocumentForm
          disabledDocumentTypes={fetchedProject?.requiredDocuments}
          isSubmitting={isAddingDocuments}
          onSubmit={handleDocumentFormSubmit}
          onCloseClicked={e => setIsAddDocumentModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isAllocationsViewOpen}>
        <ProjectAllocationForm
          project={project}
          onAllocate={handleAllocationCompleted}
          onCloseClicked={() => setIsAllocationsViewOpen(false)}
        />
      </Modal>

      <Modal isOpen={isAdditionalInformationModalOpen}>
        <ProjectAdditionalInformationForm
          project={fetchedProject}
          isLoading={isUpdatingAdditionalInformation}
          onSubmit={handleAdditionalInformationSubmit}
          onCloseClicked={() => setIsAdditionalInformationModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isAddProjectTimeEntryModalOpen}>
        <TimeEntryForm
          title={selectedProjectTimeEntry ? 'Edit Time Entry' : 'Add Time Entry'}
          shouldShowLabourerField={true}
          shouldShowProjectField={false}
          labourerSelectOptions={labourerSelectOptions}
          timeEntry={selectedProjectTimeEntry}
          isLoading={isSubmittingTimeEntryForm}
          onSubmit={handleSubmitTimeEntryForm}
          onCloseClicked={handleCloseTimeEntryFormModal}
        />
      </Modal>

      <Modal isOpen={isConfirmRemoveAllocationModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to remove ${selectedAllocation?.labourer.user.fullName} from ${selectedAllocation?.project.name}?`}
          onConfirm={handleRemoveAllocationConfirmation}
          onCancel={() => setIsConfirmRemoveAllocationModalOpen(null)}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteTimeEntryModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete this time entry?`}
          onConfirm={handleDeleteTimeEntry}
          onCancel={() => setIsConfirmDeleteTimeEntryModalOpen(false)}
        />
      </Modal>

      {fetchedProject?.qrToken && (
        <span style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
          <QRCodeCanvas
            ref={qrRef}
            value={`${process.env.REACT_APP_APP_URL}/scan/${fetchedProject.qrToken}`}
            size={200}
          />
        </span>
      )}

      <Modal isOpen={isConfirmInvalidateQrOpen}>
        <ConfirmPrompt
          message="Invalidating the QR code will immediately prevent any new scans. Are you sure?"
          isLoading={isInvalidatingQr}
          onConfirm={handleInvalidateQr}
          onCancel={() => setIsConfirmInvalidateQrOpen(false)}
        />
      </Modal>
    </div>
  );
}

export default AdminProjectView;
